/**
 * AIChat — collapsible inline AI clarification widget.
 *
 * Collapsed: "Ask AI to explain this" link with Sparkles icon.
 * Expanded: conversation history + input + send button.
 * Calls aiService.clarify() on user messages.
 * Shows a "Use this answer" button when AI returns a mapping.
 */

import { useState, useRef, useEffect } from 'react';
import { Sparkles, Send, ChevronDown, ChevronUp, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { clarify, explain, getConversationHistory } from '@/services/aiService';
import type { AIMessage, AIMapping, AnswerValue } from '@/types/rca';
import type { Question } from '@/types/rca';

// ============================================================
// Types
// ============================================================

interface AIChatProps {
  question: Question;
  questionId: string;
  onAnswer?: (value: AnswerValue) => void;
}

// ============================================================
// Sub-components
// ============================================================

function TypingIndicator() {
  return (
    <div className="flex items-center gap-1 px-3 py-2">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="w-1.5 h-1.5 rounded-full bg-cot-blue/60 animate-bounce"
          style={{ animationDelay: `${i * 0.15}s` }}
        />
      ))}
    </div>
  );
}

function MessageBubble({ msg }: { msg: AIMessage }) {
  const isUser = msg.role === 'user';
  return (
    <div className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[85%] rounded-xl px-3 py-2 text-sm leading-relaxed',
          isUser
            ? 'bg-cot-blue text-white rounded-br-sm'
            : 'bg-muted text-foreground rounded-bl-sm',
        )}
      >
        {msg.content}
      </div>
    </div>
  );
}

// ============================================================
// Component
// ============================================================

export function AIChat({ question, questionId, onAnswer }: AIChatProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<AIMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [pendingMapping, setPendingMapping] = useState<AIMapping | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Load existing conversation history when opened
  useEffect(() => {
    if (isOpen) {
      const history = getConversationHistory(questionId);
      setMessages(history);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, questionId]);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  function handleOpen() {
    setIsOpen(true);
  }

  function handleClose() {
    setIsOpen(false);
  }

  async function handleExplain() {
    setIsOpen(true);
    setIsLoading(true);
    try {
      const explanation = await explain(question);
      const aiMsg: AIMessage = { role: 'assistant', content: explanation };
      setMessages((prev) => [...prev, aiMsg]);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSend() {
    const text = inputValue.trim();
    if (!text || isLoading) return;

    const userMsg: AIMessage = { role: 'user', content: text };
    setMessages((prev) => [...prev, userMsg]);
    setInputValue('');
    setIsLoading(true);
    setPendingMapping(null);

    try {
      const result = await clarify(text, questionId, question);
      const aiMsg: AIMessage = { role: 'assistant', content: result.message };
      setMessages((prev) => [...prev, aiMsg]);
      if (result.mapping) {
        setPendingMapping(result.mapping);
      }
    } catch {
      const errMsg: AIMessage = {
        role: 'assistant',
        content: 'AI is currently unavailable. Please select an answer manually.',
      };
      setMessages((prev) => [...prev, errMsg]);
    } finally {
      setIsLoading(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  }

  function handleUseAnswer() {
    if (pendingMapping && onAnswer) {
      onAnswer(pendingMapping.answerIndex);
      setPendingMapping(null);
    }
  }

  // -------------------------------------------------------
  // Collapsed state
  // -------------------------------------------------------
  if (!isOpen) {
    return (
      <div className="flex items-center gap-3 mt-2">
        <button
          type="button"
          onClick={handleOpen}
          className="flex items-center gap-1.5 text-sm text-cot-blue hover:text-cot-blue/80 transition-colors"
        >
          <Sparkles className="w-3.5 h-3.5" />
          Ask AI to explain this
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  // -------------------------------------------------------
  // Expanded state
  // -------------------------------------------------------
  return (
    <div className="mt-4 rounded-xl border border-cot-blue/20 bg-card shadow-soft overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-cot-blue/5 border-b border-cot-blue/15">
        <div className="flex items-center gap-2 text-sm font-medium text-cot-blue">
          <Sparkles className="w-4 h-4" />
          AI Assistant
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void handleExplain()}
            className="text-xs text-muted-foreground hover:text-cot-blue transition-colors underline underline-offset-2"
          >
            Explain question
          </button>
          <button
            type="button"
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <ChevronUp className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="h-48">
        <div ref={scrollRef} className="p-4 space-y-3">
          {messages.length === 0 && !isLoading && (
            <p className="text-sm text-muted-foreground text-center py-4">
              Describe your situation and I'll help you pick the right answer.
            </p>
          )}
          {messages.map((msg, i) => (
            <MessageBubble key={i} msg={msg} />
          ))}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-muted rounded-xl rounded-bl-sm">
                <TypingIndicator />
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Mapping suggestion */}
      {pendingMapping && (
        <div className="mx-4 mb-3 flex items-center justify-between gap-3 rounded-lg bg-cot-blue/10 border border-cot-blue/20 px-3 py-2">
          <span className="text-sm text-cot-blue">
            Suggested answer:{' '}
            <strong>
              Option {String.fromCharCode(65 + pendingMapping.answerIndex).toLowerCase()}
            </strong>{' '}
            <span className="text-muted-foreground text-xs">
              ({pendingMapping.confidence} confidence)
            </span>
          </span>
          <Button
            size="sm"
            variant="default"
            onClick={handleUseAnswer}
            className="text-xs h-7 bg-cot-blue hover:bg-cot-blue/90 shrink-0"
          >
            Use this answer
          </Button>
        </div>
      )}

      {/* Input row */}
      <div className="flex items-center gap-2 px-4 pb-4">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Describe your situation..."
          disabled={isLoading}
          className={cn(
            'flex-1 h-9 rounded-lg border border-input bg-background px-3 text-sm',
            'placeholder:text-muted-foreground/60',
            'focus:outline-none focus:ring-2 focus:ring-cot-blue/50 focus:border-cot-blue',
            'disabled:opacity-50',
          )}
        />
        <Button
          type="button"
          size="icon"
          onClick={() => void handleSend()}
          disabled={!inputValue.trim() || isLoading}
          className="h-9 w-9 bg-cot-blue hover:bg-cot-blue/90 shrink-0"
        >
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
